import { createSignal, For, onMount, Show } from 'solid-js';
import { useApi, isOk } from '../api';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import { useTranslation } from '../hooks/useTranslation';
import Text from '../components/Text';
import Modal from '../components/Modal';
import { Skeleton } from '../components/Skeleton';
import { Badge, type BadgeTone } from '../components/Badge';
import { useAdminAccess } from '../admin/useAdminAccess';
import { formatDateTime } from '../services/dateFormat';
import type { ApiKeyDto } from '../api/generated/schemas';
import styles from './ApiKeysPage.module.css';
import { firstError } from '../api/envelope';

const READ = 1;
const WRITE = 2;
const ADMIN = 8;

interface KeyScope {
    permissions: number;
    label: string;
    tone: BadgeTone;
    adminOnly: boolean;
}

const KEY_SCOPES: KeyScope[] = [
    { permissions: READ, label: 'API_KEYS_SCOPE_READ', tone: 'neutral', adminOnly: false },
    { permissions: READ | WRITE, label: 'API_KEYS_SCOPE_READ_WRITE', tone: 'info', adminOnly: false },
    { permissions: READ | WRITE | ADMIN, label: 'API_KEYS_SCOPE_ADMIN', tone: 'warning', adminOnly: true },
];

// Keys created before scopes carry every permission bit and act with the owner's full access.
const LEGACY_SCOPE: Pick<KeyScope, 'label' | 'tone'> = { label: 'API_KEYS_SCOPE_FULL', tone: 'warning' };

function scopeOf(permissions: number): Pick<KeyScope, 'label' | 'tone'> {
    return KEY_SCOPES.find(scope => scope.permissions === permissions) ?? LEGACY_SCOPE;
}

export default function ApiKeysPage() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const { isAdmin } = useAdminAccess();

    const [keys, setKeys] = createSignal<ApiKeyDto[]>([]);
    const [loading, setLoading] = createSignal(true);
    const [creating, setCreating] = createSignal(false);
    const [newKeyName, setNewKeyName] = createSignal('');
    const [newKeyPermissions, setNewKeyPermissions] = createSignal(READ);
    const [createOpen, setCreateOpen] = createSignal(false);
    const [revealedKey, setRevealedKey] = createSignal<string | null>(null);
    const [copied, setCopied] = createSignal(false);

    onMount(async () => {
        const res = await api.apiKey.list();
        setLoading(false);
        if (isOk(res.data)) setKeys(res.data.value.keys);
        else toast?.error(t('API_KEYS_LOAD_ERROR'));
    });

    async function handleCreate(e: SubmitEvent) {
        e.preventDefault();
        const name = newKeyName().trim();
        if (!name) return;
        setCreating(true);
        const res = await api.apiKey.create({ name, permissions: newKeyPermissions() });
        setCreating(false);
        if (isOk(res.data)) {
            const created = res.data.value!;
            setKeys(prev => [
                { id: created.id, name: created.name, prefix: created.prefix, permissions: created.permissions, createdAt: created.createdAt, lastUsedAt: null },
                ...prev,
            ]);
            setNewKeyName('');
            setNewKeyPermissions(READ);
            setCreateOpen(false);
            setRevealedKey(created.key);
        } else {
            toast?.error(firstError(res.data) ?? t('API_KEYS_CREATE_ERROR'));
        }
    }

    async function handleRevoke(id: string, name: string) {
        if (!await showConfirm(t('API_KEYS_REVOKE_CONFIRM', name), true)) return;
        const res = await api.apiKey.delete(id);
        if (isOk(res.data)) {
            setKeys(prev => prev.filter(k => k.id !== id));
            toast?.success(t('API_KEYS_REVOKED'));
        } else {
            toast?.error(t('API_KEYS_REVOKE_ERROR'));
        }
    }

    async function handleCopy() {
        const key = revealedKey();
        if (!key) return;
        try {
            await navigator.clipboard.writeText(key);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast?.error(t('API_KEYS_COPY_ERROR'));
        }
    }

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <h1 style={{ margin: 0 }}><Text val="API_KEYS_TITLE" /></h1>
                    <p style={{ margin: 0, color: 'var(--dark-text-secondary)', 'font-size': 'var(--font-size-sm)' }}>
                        <Text val="API_KEYS_SUBTITLE" />
                    </p>
                </div>
                <button class="btn btn-primary" onClick={() => setCreateOpen(true)}>
                    <i class="fa-solid fa-plus" /> <Text val="API_KEYS_CREATE" />
                </button>
            </div>

            <Show when={loading()}>
                <div class="v" style={{ gap: 'var(--spacing-md)' }}>
                    <Skeleton height="4rem" />
                    <Skeleton height="4rem" />
                </div>
            </Show>

            <Show when={!loading() && keys().length === 0}>
                <div class={styles.emptyState}>
                    <i class="fa-solid fa-key" style={{ 'font-size': '3rem', opacity: 0.4 }} />
                    <p><Text val="API_KEYS_EMPTY" /></p>
                </div>
            </Show>

            <Show when={!loading() && keys().length > 0}>
                <div class="v" style={{ gap: 'var(--spacing-md)' }}>
                    <For each={keys()}>{(key) => (
                        <div class={`fcard ${styles.keyRow}`}>
                            <div class={styles.keyInfo}>
                                <div class="h" style={{ gap: 'var(--spacing-sm)', 'align-items': 'center', 'min-width': 0 }}>
                                    <span class={styles.keyName}>{key.name}</span>
                                    <Badge tone={scopeOf(key.permissions).tone}><Text val={scopeOf(key.permissions).label} /></Badge>
                                </div>
                                <span class={styles.keyPrefix}>
                                    <i class="fa-solid fa-key" /> {key.prefix}...
                                </span>
                            </div>
                            <div class={styles.keyMeta}>
                                <span style={{ color: 'var(--dark-text-secondary)', 'font-size': 'var(--font-size-sm)' }}>
                                    <Text val="API_KEYS_CREATED" /> {formatDateTime(key.createdAt)}
                                </span>
                                <Show when={key.lastUsedAt}>
                                    <span style={{ color: 'var(--dark-text-secondary)', 'font-size': 'var(--font-size-sm)' }}>
                                        <Text val="API_KEYS_LAST_USED" /> {formatDateTime(key.lastUsedAt!)}
                                    </span>
                                </Show>
                                <Show when={!key.lastUsedAt}>
                                    <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}>
                                        <Text val="API_KEYS_NEVER_USED" />
                                    </span>
                                </Show>
                            </div>
                            <button
                                class="btn btn-danger btn-sm"
                                onClick={() => handleRevoke(key.id, key.name)}
                                title={t('API_KEYS_REVOKE')}
                            >
                                <i class="fa-solid fa-trash" /> <Text val="API_KEYS_REVOKE" />
                            </button>
                        </div>
                    )}</For>
                </div>
            </Show>

            <Modal open={createOpen()} onClose={() => setCreateOpen(false)} title={<Text val="API_KEYS_CREATE_TITLE" />}>
                <form class="v" onSubmit={handleCreate} style={{ gap: 'var(--spacing-md)' }}>
                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                            <Text val="API_KEYS_NAME_LABEL" />
                        </span>
                        <input
                            type="text"
                            value={newKeyName()}
                            onInput={e => setNewKeyName(e.currentTarget.value)}
                            placeholder={t('API_KEYS_NAME_PLACEHOLDER')}
                            maxlength={200}
                            autofocus
                        />
                    </label>
                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                        <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                            <Text val="API_KEYS_SCOPE_LABEL" />
                        </span>
                        <select
                            value={newKeyPermissions()}
                            onChange={e => setNewKeyPermissions(parseInt(e.currentTarget.value))}
                        >
                            <For each={KEY_SCOPES.filter(scope => !scope.adminOnly || isAdmin())}>{(scope) => (
                                <option value={scope.permissions}>{t(scope.label)}</option>
                            )}</For>
                        </select>
                        <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                            <Text val="API_KEYS_SCOPE_HINT" />
                        </span>
                    </label>
                    <p style={{ margin: 0, 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}>
                        <Text val="API_KEYS_CREATE_NOTE" />
                    </p>
                    <div class="h" style={{ 'justify-content': 'flex-end', gap: 'var(--spacing-sm)' }}>
                        <button type="button" class="btn btn-secondary" onClick={() => setCreateOpen(false)}>
                            <Text val="CANCEL" />
                        </button>
                        <button type="submit" class="btn btn-primary" disabled={creating() || !newKeyName().trim()}>
                            <Show when={creating()} fallback={<><i class="fa-solid fa-plus" /> <Text val="API_KEYS_CREATE" /></>}>
                                <i class="fa-solid fa-spinner fa-spin" />
                            </Show>
                        </button>
                    </div>
                </form>
            </Modal>

            <Modal
                open={revealedKey() !== null}
                onClose={() => setRevealedKey(null)}
                title={<Text val="API_KEYS_REVEAL_TITLE" />}
            >
                <div class="v" style={{ gap: 'var(--spacing-md)' }}>
                    <p style={{ margin: 0, 'font-size': 'var(--font-size-sm)', color: 'var(--warning-500)' }}>
                        <i class="fa-solid fa-triangle-exclamation" /> <Text val="API_KEYS_REVEAL_WARNING" />
                    </p>
                    <div class={styles.keyReveal}>
                        <code class={styles.keyCode}>{revealedKey()}</code>
                        <button class="btn btn-secondary btn-sm" onClick={handleCopy}>
                            <Show when={copied()} fallback={<><i class="fa-solid fa-copy" /> <Text val="API_KEYS_COPY" /></>}>
                                <><i class="fa-solid fa-check" /> <Text val="API_KEYS_COPIED" /></>
                            </Show>
                        </button>
                    </div>
                    <div class="h" style={{ 'justify-content': 'flex-end' }}>
                        <button class="btn btn-primary" onClick={() => setRevealedKey(null)}>
                            <Text val="API_KEYS_REVEAL_DONE" />
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
